import type { FC } from "react";
import React, { useEffect, useMemo, useState } from "react";
import {
   Box,
   Card,
   FlexList,
   H2,
   Title,
   H1,
   Form,
   Page,
   Spinner,
   useI18nContext,
   Flex,
   Button,
   Required,
} from "@procore/core-react";
import type { Comments, ProjectOptions } from "../types";
import { DropDownType } from "../types";
import { formatOptions } from "@/react/shared/helper";
import { useGetAssignmentCreationSupportDataQuery, useGetAssignmentDetails } from "./queries";
import { processCustomFieldsFormValues } from "@/react/shared/custom-field-utils";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { useDropDownSelectionHandler } from "@/react/shared/hooks/useDropDownSelectionHandler";
import { DEFAULT_OVERTIME_RATES, formInitialValues } from "./constants";
import CustomFieldsCard from "../shared/custom-fields-card";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { useGetCustomFields } from "../../common/queries/queries";
import CommentsCard from "../shared/comments-card";
import type { FormikProps } from "formik";
import AdditionalInstructionsCard from "../shared/additional-instructions-card";
import { ScheduleCard } from "../shared/schedule-card";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import type { AssignmentFormValues } from "./types";
import { workDays } from "@/react/shared/constants";
import type { WorkdayOptions } from "../../labor-plans/prop-types";
import { getAttachedDate } from "@laborchart-modules/common/dist/datetime/date";
import { getLastWorkDate } from "@/lib/utils/date-2";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import type { BatchCreateAssignmentsPayload } from "@laborchart-modules/lc-core-api/dist/api/assignments/create-assignment";
import { getCommonFieldsForRequestAndAssignment } from "../helper";
import { assignmentSchema } from "./validation-schemas";
import type { CustomField } from "@/react/prop-types";
import { useAssignmentTearsheet } from "./assignment-tearsheet";
import type { AlertContext } from "@laborchart-modules/common/dist/rethink/schemas/enums/alerts";

export interface TransferAssignmentsTearsheetProps {
   onClose: () => void;
   showProjectDetail?: boolean;
   projectId: string;
   endDay: number;
   assignments: Array<{
      id: string;
      type: string;
      resourceId: string;
      resourceName: string;
      workDays: WorkdayOptions;
      startDay: number;
      endDay: number;
      recipientIds: string[];
   }>;
   callback: (data: any) => void;
   // categoryId/subcategoryId: undefined = use DB data, null = explicitly no category, string = use this category
   categoryId?: string | null;
   subcategoryId?: string | null;
}

export interface TransferAssignment {
   id: string;
   type: string;
   resourceId: string;
   resourceName: string;
   workDays: WorkdayOptions;
   startDay: number;
   endDay: number;
   recipientIds: string[];
}

async function handleSubmit(
   values: AssignmentFormValues,
   assignments: TransferAssignment[],
   endDay: number,
   customFields: CustomField[],
   callback: (data: any) => void,
   onClose?: () => void,
) {
   // End the original assignments on endDay - 1;
   const deleteAssignmentPromises: Array<ReturnType<typeof Assignment2Store.deleteAssignment>> = [];
   const updateAssignmentPayload: Array<{ id: string; end_day?: number }> = [];
   assignments.forEach((assn: TransferAssignment) => {
      const workDays = assn.workDays;
      const assnStartDate = getAttachedDate(assn.startDay);
      const newEndDay = getLastWorkDate({
         fromDate: getAttachedDate(endDay),
         workDays,
      });
      if (newEndDay && newEndDay < assnStartDate) {
         deleteAssignmentPromises.push(Assignment2Store.deleteAssignment(assn.id));
      } else {
         updateAssignmentPayload.push({
            id: assn.id,
            end_day: newEndDay ? getDetachedDay(newEndDay) : undefined,
         });
      }
   });
   // Create a new assignment for each resource using batch create assignment API
   const commonCreateFields = getCommonFieldsForRequestAndAssignment(values, customFields);
   const batchCreateAssignmentPayload: BatchCreateAssignmentsPayload = {
      resource_ids: (values.resource as any[]).map((resource: any) => resource.id),
      ...commonCreateFields,
   };

   const createAssignmentPromise = Assignment2Store.createBatchAssignment(
      batchCreateAssignmentPayload,
   ).payload;
   const [createResult] = await Promise.all([
      createAssignmentPromise,
      ...deleteAssignmentPromises,
      updateAssignmentPayload.length > 0
         ? Assignment2Store.updateAssignmentsStream(updateAssignmentPayload)
         : Promise.resolve(),
   ]);

   const callbackData = createResult.data.map((createdAssignment: any) => ({
      ...createdAssignment,
      name: assignments.find(
         (assignment: any) => assignment.resourceId === createdAssignment.resource_id,
      )?.resourceName,
   }));
   callback(callbackData);
   onClose?.();
   return createResult;
}

export const TransferAssignmentsTearsheetContent: FC<TransferAssignmentsTearsheetProps> = ({
   onClose,
   projectId, // its an optional property so it can be undefined will be defined when create Assignment button is clicked from project detail page
   assignments,
   endDay,
   callback,
   categoryId,
   subcategoryId,
}) => {
   const laborResources = useMemo(
      () =>
         assignments
            .filter((assignment: any) => assignment.type === "assignment")
            .map((assignment: any) => ({
               id: assignment.resourceId,
               label: assignment.resourceName,
            })),
      [assignments],
   );
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const { data: assignmentCreationSupportData, isLoading: supportDataLoading } =
      useGetAssignmentCreationSupportDataQuery(groupId);
   const {
      handleSelection,
      categoryOptions,
      subCategoryOptions,
      categoryPlaceHolder,
      subCategoryPlaceHolder,
      resetSubCategory,
      setTotalHours,
      projectProcoreId,
   } = useDropDownSelectionHandler(assignmentCreationSupportData);
   const { data: customFields } = useGetCustomFields(CustomFieldEntity.ASSIGNMENT);
   const { checkAuthAction } = usePermissionContext();
   const canViewAllStatuses = useMemo(
      () => checkAuthAction(AuthAction.CAN_VIEW_ALL_STATUSES),
      [checkAuthAction],
   );
   // Fetch source assignment details when transferring a single assignment
   const singleAssignmentId = assignments.length === 1 ? assignments[0].id : null;
   const { data: sourceAssignmentData, isLoading: sourceAssignmentLoading } =
      useGetAssignmentDetails(singleAssignmentId);

   // Wait for source assignment data when transferring a single assignment
   const isLoadingSourceData = Boolean(singleAssignmentId && sourceAssignmentLoading);

   // Compute initial values including source data (when available for single assignment)
   const computedInitialValues = useMemo((): AssignmentFormValues => {
      const supportData = assignmentCreationSupportData?.data;

      // Find the project option to get its label
      const projectOption = supportData?.project_options?.find((p: any) => p.id === projectId);

      // Find category label from grouped_cost_code_options if categoryId is provided
      let categoryOption = null;
      if (categoryId && supportData?.grouped_cost_code_options?.[projectId]) {
         categoryOption = supportData.grouped_cost_code_options[projectId].find(
            (c: any) => c.id === categoryId,
         );
      }

      // Find subcategory label from grouped_label_options if subcategoryId is provided
      let subcategoryOption = null;
      if (subcategoryId && categoryId && supportData?.grouped_label_options?.[categoryId]) {
         subcategoryOption = supportData.grouped_label_options[categoryId].find(
            (s: any) => s.id === subcategoryId,
         );
      }

      const baseValues: AssignmentFormValues = {
         ...formInitialValues(projectId, categoryId, subcategoryId, laborResources),
         start_date: getAttachedDate(endDay),
         // Override with proper labels if found
         project: projectOption
            ? { id: projectId, label: projectOption.name }
            : { id: projectId, label: "" },
         category: categoryOption
            ? { id: categoryId!, label: categoryOption.name }
            : categoryId
            ? { id: categoryId, label: "" }
            : null,
         subcategory: subcategoryOption
            ? { id: subcategoryId!, label: subcategoryOption.name }
            : subcategoryId
            ? { id: subcategoryId, label: "" }
            : null,
      };

      // Pre-populate from source assignment if single assignment transfer
      if (sourceAssignmentData?.data) {
         const sourceData = sourceAssignmentData.data;
         const customFieldValues = processCustomFieldsFormValues(sourceData.custom_fields);

         return {
            ...baseValues,
            percent_allocated: sourceData.percent_allocated ?? baseValues.percent_allocated,
            instruction_text: sourceData.instruction_text ?? baseValues.instruction_text,
            work_scope_text: sourceData.work_scope_text ?? baseValues.work_scope_text,
            assignment_by_time: sourceData.start_time != null && sourceData.end_time != null,
            assignment_by_allocation: Boolean(sourceData.percent_allocated),
            ...customFieldValues,
         };
      }

      return baseValues;
   }, [
      projectId,
      categoryId,
      subcategoryId,
      laborResources,
      endDay,
      sourceAssignmentData,
      assignmentCreationSupportData,
   ]);

   const [selectedWorkDays, setSelectedWorkDays] = useState<WorkdayOptions>(workDays);
   const [assignmentByTime, setAssignmentByTime] = useState(
      Boolean(computedInitialValues.assignment_by_time),
   );
   const [assignmentByAllocation, setAssignmentByAllocation] = useState(
      Boolean(computedInitialValues.assignment_by_allocation),
   );
   const [isOverTimeSelected, setIsOverTimeSelected] = useState<boolean>(false);
   const [makeTotalHoursAssignment] = useState<boolean>(true);

   const { dispatch } = useAssignmentTearsheet();
   const commentList: Comments[] = [];
   const processedProjectIdRef = React.useRef<string | null>(null);

   // Track whether we've successfully initialized with support data
   const hasInitializedWithDataRef = React.useRef<boolean>(false);

   useEffect(() => {
      const initializeDropdowns = async () => {
         const currentProjectId = computedInitialValues.project?.id;
         const hasData = Boolean(assignmentCreationSupportData?.data);

         // Need to initialize if:
         // 1. Project ID changed, OR
         // 2. Support data just became available and we haven't initialized yet
         const shouldInitialize =
            currentProjectId &&
            hasData &&
            (currentProjectId !== processedProjectIdRef.current ||
               !hasInitializedWithDataRef.current);

         if (shouldInitialize) {
            // Await project selection to ensure categoryOptions are populated
            await handleSelection(
               computedInitialValues.project,
               DropDownType.PROJECT,
               makeTotalHoursAssignment,
            );
            processedProjectIdRef.current = currentProjectId;
            hasInitializedWithDataRef.current = true;

            // Now that project is selected and categories are loaded, set category if provided
            if (computedInitialValues.category) {
               await handleSelection(computedInitialValues.category, DropDownType.CATEGORY);
            }
         }
      };

      initializeDropdowns();
   }, [
      computedInitialValues.project?.id,
      computedInitialValues.category,
      assignmentCreationSupportData?.data,
      makeTotalHoursAssignment,
      handleSelection,
   ]);

   useEffect(() => {
      setAssignmentByTime(Boolean(computedInitialValues.assignment_by_time));
      setAssignmentByAllocation(Boolean(computedInitialValues.assignment_by_allocation));
   }, [computedInitialValues.assignment_by_time, computedInitialValues.assignment_by_allocation]);

   const validationSchema = assignmentSchema(
      I18n,
      !canViewAllStatuses,
      selectedWorkDays,
      assignmentByAllocation,
      assignmentByTime,
      projectProcoreId !== null,
      false,
      isOverTimeSelected,
      true,
   );

   return (
      <Spinner loading={supportDataLoading || isLoadingSourceData}>
         <Page>
            <Page.Main
               style={{
                  width: "950px",
                  display: "flex",
                  flexDirection: "column",
               }}
            >
               <Page.Header>
                  <Page.Title>
                     <H1>
                        {I18n.t("views.company.workforce_planning.transfer_assignment.title", {
                           count: assignments.length,
                        })}
                     </H1>
                  </Page.Title>
               </Page.Header>
               <Form
                  view="create"
                  enableReinitialize
                  onSubmit={(values: AssignmentFormValues) =>
                     handleSubmit(
                        values,
                        assignments,
                        endDay,
                        customFields ?? [],
                        callback,
                        onClose,
                     )
                  }
                  initialValues={computedInitialValues}
                  validationSchema={validationSchema}
               >
                  {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
                  {({
                     submitForm,
                     isSubmitting,
                     setFieldValue,
                     errors,
                     values,
                     validateForm,
                  }: FormikProps<any>) => {
                     const setInitialValuesAdapter = (
                        updater: React.SetStateAction<AssignmentFormValues>,
                     ) => {
                        if (typeof updater === "function") {
                           const nextValues = updater(values);
                           if (nextValues.start_date !== values.start_date) {
                              setFieldValue("start_date", nextValues.start_date);
                           }
                           return;
                        }

                        if (updater.start_date !== values.start_date) {
                           setFieldValue("start_date", updater.start_date);
                        }
                     };

                     return (
                        <>
                           <Page.Body style={{ marginBottom: "auto" }}>
                              <Card style={{ marginBottom: "10px" }}>
                                 <Box padding="md">
                                    <FlexList justifyContent="space-between" marginBottom="sm">
                                       <Title>
                                          <Title.Text>
                                             <H2>
                                                {I18n.t("views.company.workforce_planning.details")}
                                             </H2>
                                          </Title.Text>
                                       </Title>
                                    </FlexList>
                                    <Form.Form>
                                       <Form.Row>
                                          <Form.Select
                                             colStart={1}
                                             colWidth={12}
                                             name="project"
                                             label={I18n.t(
                                                "views.company.workforce_planning.project",
                                             )}
                                             required
                                             options={formatOptions(
                                                assignmentCreationSupportData?.data
                                                   .project_options ?? [],
                                             )}
                                             onSelect={({ item }) => {
                                                const selectedItem =
                                                   assignmentCreationSupportData?.data.project_options.find(
                                                      (project) => project.id === item.id,
                                                   ) as ProjectOptions;
                                                handleSelection(selectedItem, DropDownType.PROJECT);
                                             }}
                                          />
                                       </Form.Row>
                                       <Form.Row>
                                          <Form.Select
                                             colStart={1}
                                             colWidth={6}
                                             name="category"
                                             label={I18n.t(
                                                "views.company.workforce_planning.category",
                                             )}
                                             options={categoryOptions || []}
                                             onSelect={({ item }) => {
                                                handleSelection(item, DropDownType.CATEGORY);
                                                setFieldValue("subcategory", null);
                                             }}
                                             placeholder={I18n.t(categoryPlaceHolder)}
                                             disabled={categoryOptions.length === 0}
                                             onClear={() => {
                                                resetSubCategory();
                                                setFieldValue("subcategory", null);
                                             }}
                                          />
                                          <Form.Select
                                             colStart={7}
                                             colWidth={6}
                                             name="subcategory"
                                             label={I18n.t(
                                                "views.company.workforce_planning.subcategory",
                                             )}
                                             options={subCategoryOptions}
                                             placeholder={I18n.t(subCategoryPlaceHolder)}
                                             disabled={subCategoryOptions.length === 0}
                                          />
                                       </Form.Row>
                                       <Form.Row>
                                          <Form.MultiSelect
                                             colStart={1}
                                             colWidth={6}
                                             name="resource"
                                             label={I18n.t(
                                                "views.company.workforce_planning.labor",
                                             )}
                                             options={formatOptions(
                                                assignmentCreationSupportData?.data
                                                   .resource_options ?? [],
                                             )}
                                             placeholder={I18n.t(
                                                "views.company.workforce_planning.assignment.labor_placeholder",
                                             )}
                                             onChange={(item) => {
                                                handleSelection(item, DropDownType.RESOURCE);
                                             }}
                                             disabled={true}
                                             required={true}
                                          />
                                       </Form.Row>
                                    </Form.Form>
                                 </Box>
                              </Card>
                              <ScheduleCard
                                 I18n={I18n}
                                 setFieldValue={setFieldValue}
                                 assignmentCreationSupportData={assignmentCreationSupportData}
                                 selectedWorkDays={selectedWorkDays}
                                 setSelectedWorkDays={setSelectedWorkDays}
                                 assignmentByTime={assignmentByTime}
                                 setAssignmentByTime={(value: boolean) => {
                                    setAssignmentByTime(value);
                                    setFieldValue("assignment_by_time", value);
                                 }}
                                 assignmentByAllocation={assignmentByAllocation}
                                 setAssignmentByAllocation={(value: boolean) => {
                                    setAssignmentByAllocation(value);
                                    setFieldValue("assignment_by_allocation", value);
                                 }}
                                 canViewAllStatuses={canViewAllStatuses}
                                 values={values} // this will contain the form values
                                 overTimeProps={{
                                    showOverTime: true,
                                    isOverTimeSelected,
                                    setIsOverTimeSelected,
                                    defaultOverTimeRates:
                                       assignmentCreationSupportData?.data.overtime_day_rates ??
                                       DEFAULT_OVERTIME_RATES,
                                    errors,
                                    setTotalHours,
                                 }}
                                 setInitialValues={setInitialValuesAdapter}
                                 isCreateFlow={false}
                              />
                              <CustomFieldsCard
                                 customFields={customFields ?? []}
                                 I18n={I18n}
                                 filterIntegrationOnly={false}
                              />
                              <AdditionalInstructionsCard />
                              <CommentsCard comments={commentList} />
                           </Page.Body>
                           <Page.Footer>
                              <Box padding="md">
                                 <Flex justifyContent="space-between" alignItems="center">
                                    <Required showLabel />
                                    <FlexList space="sm">
                                       <Button
                                          variant="tertiary"
                                          disabled={isSubmitting}
                                          onClick={onClose}
                                       >
                                          {I18n.t("views.company.workforce_planning.cancel")}
                                       </Button>
                                       <Button
                                          variant="tertiary"
                                          disabled={isSubmitting}
                                          onClick={async () => {
                                             const errors = validateForm(values);
                                             if (Object.keys(errors).length > 0) {
                                                return;
                                             }

                                             const newAssignments = await handleSubmit(
                                                values,
                                                assignments,
                                                endDay,
                                                customFields ?? [],
                                                callback,
                                             );
                                             dispatch({
                                                type: "open-send-assignment-alerts",
                                                projectId: projectId,
                                                assignments: newAssignments.data.map(
                                                   (assignment: any) => ({
                                                      id: assignment.id,
                                                      recipientIds: [assignment.resource_id],
                                                   }),
                                                ),
                                                alertType: "assignment-transfer" as AlertContext,
                                             });
                                          }}
                                       >
                                          {I18n.t(
                                             "views.company.workforce_planning.transfer_assignment.transfer_and_alert",
                                          )}
                                       </Button>
                                       <Button
                                          type="submit"
                                          disabled={isSubmitting}
                                          onClick={submitForm}
                                       >
                                          {I18n.t(
                                             "views.company.workforce_planning.transfer_assignment.transfer",
                                          )}
                                       </Button>
                                    </FlexList>
                                 </Flex>
                              </Box>
                           </Page.Footer>
                        </>
                     );
                  }}
               </Form>
            </Page.Main>
         </Page>
      </Spinner>
   );
};
